/**
 * Knowledge-base facts + gaps derived from the asset workflow (1.10). Handoff pattern:
 * fact cards with IDs, HIGH/MED/LOW/GAP confidence and evidence chips; gap cards with
 * what blocks / what closes it. No multi-model consensus (out of v1 scope).
 */
export function deriveKb(wf, reviews = {}) {
  const facts = [];
  const gaps = [];
  const wells = wf.wells || [];
  const s = wf.summary || {};
  const prov = Object.fromEntries((wf.provenance || []).map((p) => [p.adapter, p]));
  const ev = (adapter, extra) => ({ label: `${prov[adapter]?.source || adapter}${extra ? ` · ${extra}` : ''}`, sha: prov[adapter]?.sha256 || null });
  let n = 0;
  const id = () => `F-${String((n += 1)).padStart(2, '0')}`;

  const leaseHistory = s.historyGrain === 'lease';
  const historyCount = s.withHistory || 0;
  const historyNoun = leaseHistory
    ? (historyCount === 1 ? 'lease' : 'leases')
    : (historyCount === 1 ? 'well' : 'wells');
  const historyVerb = historyCount === 1 ? 'has' : 'have';
  facts.push({
    id: id(),
    section: 'Production',
    title: `${historyCount} ${historyNoun} ${historyVerb} monthly ${leaseHistory ? 'lease-level ' : ''}production history`,
    confidence: historyCount ? 'HIGH' : 'GAP',
    evidence: ['aries-duckdb', 'production-csv'].filter((a) => prov[a]).map((a) => ev(a, a === 'aries-duckdb' ? 'AC_PRODUCT' : (prov[a]?.sourceLabel || ''))),
  });
  facts.push({ id: id(), section: 'Forecasts', title: `${s.fitted} wells fitted (A ${s.qualityCounts?.A} · B ${s.qualityCounts?.B} · C ${s.qualityCounts?.C})`, confidence: s.qualityCounts?.C > s.fitted / 2 ? 'MED' : 'HIGH', evidence: [{ label: 'D2D multi-well DCA', sha: null }] });
  if (s.ariesRemainingTotal) {
    const d = (s.remainingTotal / s.ariesRemainingTotal - 1) * 100;
    facts.push({ id: id(), section: 'Forecasts', title: `D2D remaining is ${d >= 0 ? '+' : ''}${d.toFixed(0)}% vs the seller forecast case`, confidence: Math.abs(d) <= 20 ? 'HIGH' : 'MED', evidence: [ev('aries-duckdb', 'AC_ECONOMIC'), { label: 'D2D DCA', sha: null }] });
  }
  facts.push({
    id: id(),
    section: 'Locations',
    title: leaseHistory
      ? `${s.withLocation}/${s.wells} leases located`
      : `${s.withLocation}/${s.wells} wells located (joined on API-10)`,
    confidence: s.withLocation === s.wells ? 'HIGH' : 'MED',
    evidence: [ev('well-locations-json')],
  });
  if (wf.priceDeck) facts.push({ id: id(), section: 'Pricing', title: `Price deck: ${wf.priceDeck.label}`, confidence: 'HIGH', evidence: [ev('aries-duckdb', 'AR_SIDEFILE')] });
  const approved = Object.values(reviews).filter((r) => r.status === 'approved').length;
  facts.push({ id: id(), section: 'Approvals', title: `${approved}/${s.withHistory} forecasts approved`, confidence: approved === s.withHistory && approved ? 'HIGH' : approved ? 'MED' : 'GAP', evidence: [{ label: 'forecast_review_audit', sha: null }] });
  (wf.facts || []).slice(0, 12).forEach((f) => facts.push({ id: id(), section: 'Documents', title: `${f.subject}: ${f.value?.files ?? '?'} well files (${(f.value?.tags || []).join(', ')})`, confidence: f.confidence, evidence: (f.evidence || []).map((e) => ({ label: e, sha: null })) }));

  const big = wells.filter((w) => w.compare && Math.abs(w.compare.remainingDeltaPct) >= 50 && (w.aries?.remaining || 0) > 50000);
  if (big.length) gaps.push({ id: id(), section: 'Forecasts', title: `${big.length} wells differ ≥50% from the seller forecast remaining`, blocks: 'Collateral volumes / bid PV', closes: 'Engineer review + approve (D2D, seller forecast, or override) per well', owner: 'Desk engineer', wells: big.map((w) => w.assetId) });
  const noFit = wells.filter((w) => w.history?.length && w.fit?.status !== 'ok');
  if (noFit.length) gaps.push({ id: id(), section: 'Forecasts', title: `${noFit.length} wells with history but no Arps fit`, blocks: 'Type curve / EUR roll-up', closes: 'Manual override with reason', owner: 'Desk engineer', wells: noFit.map((w) => w.assetId) });
  const poor = wells.filter((w) => w.fit?.quality === 'C');
  if (poor.length) gaps.push({ id: id(), section: 'Forecasts', title: `${poor.length} low-confidence fits (grade C)`, blocks: 'Approval', closes: 'Review window / override', owner: 'Desk engineer', wells: poor.map((w) => w.assetId) });
  const unloc = wells.filter((w) => !Number.isFinite(w.lat));
  if (unloc.length) gaps.push({
    id: id(),
    section: 'Locations',
    title: leaseHistory
      ? `${unloc.length} ${unloc.length === 1 ? 'lease' : 'leases'} without coordinates`
      : `${unloc.length} wells without coordinates`,
    blocks: 'Map / pool selection',
    closes: leaseHistory ? 'Lease surface locations' : 'State well records by API',
    owner: 'Data',
    wells: unloc.map((w) => w.assetId),
  });
  const orphans = (wf.issues || []).filter((i) => i.kind === 'orphanProduction' || i.kind === 'forecastWithoutProperty');
  if (orphans.length) gaps.push({ id: id(), section: 'Production', title: `${orphans.length} production rows not linked to a property`, blocks: 'Complete roll-up', closes: 'Seller to confirm the well id mapping', owner: 'Seller / advisor', wells: [] });
  if (!s.ariesRemainingTotal) gaps.push({ id: id(), section: 'Forecasts', title: 'No seller forecast case ingested', blocks: 'Seller vs D2D comparison', closes: 'Add the seller forecast case to the data room', owner: 'Seller', wells: [] });

  const sections = ['Production', 'Forecasts', 'Locations', 'Pricing', 'Approvals', 'Documents'].map((name) => {
    const fs = facts.filter((f) => f.section === name);
    const gs = gaps.filter((g) => g.section === name);
    const score = fs.reduce((a, f) => a + ({ HIGH: 1, MED: 0.6, LOW: 0.3, GAP: 0 })[f.confidence] || 0, 0);
    const pct = fs.length + gs.length ? Math.round((score / (fs.length + gs.length)) * 100) : 0;
    return { name, pct, facts: fs.length, gaps: gs.length };
  });
  return { facts, gaps, sections };
}
